import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { GoogleIcon } from '@/components/icons';
import { env } from '@/config/env';
import { useGoogleSdk } from '@/lib/google/useGoogleSdk';

interface Props {
  /** Recebe o ID token (JWT do Google) quando a pessoa conclui o consentimento. */
  onCredential: (idToken: string) => void;
  /** 'signin_with' na tela de login, 'signup_with' na de cadastro. */
  text?: 'signin_with' | 'signup_with';
  className?: string;
}

/**
 * Botão "Entrar com Google".
 *
 * 🔑 Quem desenha o botão é o Google, não nós. No fluxo de ID token — o que evita
 * guardar `client_secret` na VPS e registrar URL de redirecionamento — o token só sai
 * pelo botão que o próprio SDK renderiza. Um botão nosso exigiria o fluxo de código,
 * que é mais servidor, mais segredo e mais coisa para quebrar em produção.
 *
 * Sem `VITE_GOOGLE_CLIENT_ID` a tela mostra o nosso botão desabilitado, e não um
 * espaço vazio: o mesmo padrão do "Conectar via Facebook". Um botão sumindo lê-se como
 * bug do app; desabilitado com motivo lê-se como o que é — falta de configuração.
 */
export function GoogleButton({ onCredential, text = 'signin_with', className }: Props) {
  const { load } = useGoogleSdk();
  const containerRef = useRef<HTMLDivElement>(null);
  const [falhou, setFalhou] = useState(false);
  // O callback do Google é registrado uma vez, mas `onCredential` muda a cada render
  // do pai. Sem a ref, o SDK ficaria chamando a primeira versão da função para sempre.
  const callbackRef = useRef(onCredential);
  callbackRef.current = onCredential;

  const clientId = env.VITE_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!clientId) return;
    let cancelado = false;

    load()
      .then((google) => {
        if (cancelado || !containerRef.current) return;
        google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response.credential) callbackRef.current(response.credential);
          },
          // Sem One Tap automático: entrar é decisão de quem chega, e um pop-up que
          // aparece sozinho na tela de login atrapalha quem veio usar a senha.
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text,
          shape: 'rectangular',
          logo_alignment: 'left',
          width: 360,
          locale: 'pt-BR',
        });
      })
      .catch(() => {
        if (!cancelado) setFalhou(true);
      });

    return () => {
      cancelado = true;
    };
  }, [clientId, load, text]);

  if (!clientId || falhou) {
    return (
      <Button
        type="button"
        variant="secondary"
        size="lg"
        disabled
        className={className}
        leadingIcon={<GoogleIcon size={19} />}
        title={
          clientId
            ? 'Não foi possível carregar o Google. Verifique sua conexão e recarregue a página.'
            : 'Login com Google não configurado neste ambiente.'
        }
      >
        Entrar com Google
      </Button>
    );
  }

  // O Google injeta o botão dentro deste nó. `min-height` evita o pulo de layout
  // entre a montagem e o SDK responder.
  return (
    <div
      ref={containerRef}
      data-testid="google-button"
      className={className}
      style={{ minHeight: 44 }}
    />
  );
}
